// ─── Flujo piloto, fase 4: la remisión (RS) y la factura (FV) desde el pedido ─
// Como `opPvRsPreparar` del panel de ventas: por cada renglón del pedido,
//   debe  = pedido − despachado
//   tope  = lo que se puede despachar HOY = min(debe, lo que el piloto le
//           asignó en bodega a ESTE pedido —lo apartado más lo libre que le
//           toca—, lo disponible para el flujo de siempre)
// y cuando el tope no alcanza, el renglón dice por qué (en camino, pedido a
// compras, sin pedir, apartado que ya no está). La regla dura del panel: nunca
// despachar lo que no está disponible; lo que se pide de más se rechaza, no se
// recorta en silencio.
//
// Un kit se despacha por el artículo vendido, pero lo que manda es la parte que
// menos alcance (cuántos se pueden ARMAR con lo asignado de cada parte).
//
// Es puro: la cadena y las filas de Material en transición llegan calculadas.

const num = (v) => { const n = Number(String(v ?? "").replace(/,/g, ".")); return Number.isFinite(n) ? n : 0; };
const EPS = 1e-6;

// `disponible(op, articulo)` (opcional): lo que el flujo de siempre deja
// despachar (descuenta lo facturado por adelantado a otros clientes). Para un
// kit, cuántos se pueden armar.
export function preparacionRS(cad, filas, disponible) {
  const mias = (filas || []).filter(f => f.cad.op.id === cad.op.id);
  return cad.lineas.filter(l => l.debe > EPS).map(l => {
    const deLinea = mias.filter(f => f.linea.idx === l.idx);
    let asignado, limita = null;
    if (l.esKit) {
      asignado = Infinity;
      deLinea.forEach(f => {
        const arm = f.c.porUnidad > 0 ? Math.floor((f.enBodega + EPS) / f.c.porUnidad) : 0;
        if (arm < asignado) { asignado = arm; limita = f; }
      });
      if (!deLinea.length) asignado = 0;
    } else {
      limita = deLinea[0] || null;
      asignado = limita ? limita.enBodega : 0;
    }
    const flujo = typeof disponible === "function" ? Math.max(0, num(disponible(cad.op, l.articulo))) : Infinity;
    const tope = Math.max(0, Math.min(l.debe, asignado, flujo));
    const partes = [];
    if (tope + EPS < l.debe) {
      if (flujo + EPS < Math.min(l.debe, asignado)) partes.push(`el flujo de siempre solo deja ${flujo} (lo demás está facturado a otro cliente)`);
      const f = limita;
      if (f) {
        const pre = l.esKit ? `${f.c.articulo}: ` : "";
        if (f.apartadoSinStock > EPS) partes.push(`${pre}${f.apartadoSinStock} apartados ya no están en bodega`);
        if (f.comprado > EPS) partes.push(`${pre}${f.comprado} vienen en camino`);
        if (f.enRQ > EPS) partes.push(`${pre}${f.enRQ} pedidos a compras`);
        if (f.sinPedir > EPS) partes.push(`${pre}${f.sinPedir} sin pedir`);
      }
    }
    return {
      idx: l.idx, articulo: l.articulo, descripcion: l.descripcion, debe: l.debe, tope,
      motivo: partes.join(" · "), esKit: l.esKit
    };
  });
}

// La remisión que se va a armar: solo renglones con cantidad, y nunca más del
// tope. Devuelve los errores en vez de recortar.
export function armarRS(prep, cantidades) {
  const errores = [];
  const items = [];
  prep.forEach(p => {
    const c = num(cantidades && cantidades[p.idx] != null && cantidades[p.idx] !== "" ? cantidades[p.idx] : p.tope);
    if (c <= EPS) return;
    if (c > p.tope + EPS) { errores.push(`${p.articulo}: se pidieron ${c} y solo se pueden despachar ${p.tope}${p.motivo ? ` (${p.motivo})` : ""}`); return; }
    items.push({ articulo: p.articulo, descripcion: p.descripcion || "", cantidad: c });
  });
  if (!items.length && !errores.length) errores.push("No hay nada que despachar: escribe una cantidad en al menos un renglón.");
  return { items, errores };
}

// ─── Lo despachado que falta facturar ───────────────────────────────────────
// Remisiones que ya salieron de bodega (ni borrador ni cancelada) y que ninguna
// factura vigente cubre.
export function remisionesPorFacturar(cad) {
  const facturadas = new Set();
  (cad.docs.facturas || []).forEach(f => [f._origen, ...(Array.isArray(f._origenes) ? f._origenes : [])]
    .filter(Boolean).forEach(o => { if (o.tipo === "RC") facturadas.add(o.id); }));
  return (cad.docs.despachos || []).filter(rc => rc.estado !== "facturada" && !facturadas.has(rc.id));
}

export const remisionesEnBorrador = (cad) => (cad.docs.remisiones || []).filter(rc => rc.estado === "borrador");
